/* Keeping the header cart count true.
 *
 * The badge is the one number on screen whose entire job is to be current,
 * and it was not. Horizon re-renders the cart section when a quantity
 * changes, but the header is a different section and nothing told it: edit a
 * line on the cart page and the badge kept the old number until the next
 * full page load.
 *
 * This has its own file, and its own <script> tag in the header section,
 * because it is needed on every page while the add-to-cart module is loaded
 * only by the product section - so it was absent from the cart page, which
 * is precisely where quantities get edited.
 *
 * It is loaded rather than imported on purpose. A relative import resolves
 * to an asset URL with no ?v= on it, which is the one thing Shopify's asset
 * URLs exist to provide: the unversioned copy sits in the CDN and the
 * browser cache, and an edit to this file would take an unknowable time to
 * reach anyone - this was found the hard way, debugging a fix that had
 * already been pushed. `@theme/` specifiers are not an alternative; Shopify
 * builds that import map from its own modules and will not add ours.
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Replay a one-shot animation that may already have run. */
const sparkle = (el) => {
  if (!el) return;
  el.classList.remove('ksg-sparkle-once');
  void el.offsetWidth; /* forces the class removal to take effect first */
  el.classList.add('ksg-sparkle-once');
};

/* The cart link is re-rendered rather than patched. Its label is a
   pluralised, translated string - "1 item" against "2 items" against Thai,
   which has no plural at all - and rebuilding that in JavaScript would mean
   writing the copy twice, in a language the locale files cannot see. The
   Section Rendering API hands back the same markup Liquid would have
   produced, already in the right locale. */
const countOf = (el) => (el ? (el.querySelector('.ksg-cart-count') || {}).textContent || '0' : null);

/* Asks for the header again, and will ask more than once.
 *
 * The event announces that the cart is changing, not that the change has
 * been written, so the first render can come back describing the cart as it
 * was. Rather than guess a delay long enough to win that race - which is a
 * guess that fails on a slow connection, the one place it matters - the
 * answer is checked: a render still showing the count already on screen is
 * treated as too early and asked for again, backing off each time. A cart
 * edit that genuinely leaves the count alone just costs three quiet
 * requests and settles on the same number.
 *
 * The delays are the design system's motion durations, which is where the
 * theme keeps its timings. */
const ATTEMPTS = [0, 220, 420, 900];

const refreshCartLink = async () => {
  const link = document.querySelector('.ksg-cart-link');
  const section = document.querySelector('.ksg-header')?.closest('.shopify-section');
  if (!link || !section) return;

  const id = section.id.replace('shopify-section-', '');
  const before = countOf(link);

  try {
    let fresh = null;

    for (let i = 0; i < ATTEMPTS.length; i++) {
      if (ATTEMPTS[i]) await new Promise((r) => setTimeout(r, ATTEMPTS[i]));

      const res = await fetch(`${root}?sections=${encodeURIComponent(id)}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;

      const data = await res.json();
      const html = new DOMParser().parseFromString(data[id] || '', 'text/html');
      const candidate = html.querySelector('.ksg-cart-link');
      if (!candidate) return;

      fresh = candidate;
      if (countOf(candidate) !== before) break;
    }

    if (!fresh) return;

    link.innerHTML = fresh.innerHTML;

    /* The sparkle lives here rather than in the add-to-cart module because
       this is the code that replaces the icon: anything animating the old
       node is animating something about to be thrown away. The rule is
       simply that the mark moves whenever the count changes, which is one
       rule instead of two and true on the cart page as well as the product
       page. Reduced motion switches it off, which is why the number, the
       label and the toast all change too. */
    sparkle(link.querySelector('svg'));
  } catch {
    /* The count is a nicety; a stale one is not worth a broken add. */
  }
};

/* Two names. `shopify:cart:lines-update` is the platform's own event, so the
   quantity steppers, the remove control and the cart drawer are all covered
   without this knowing about any of them. `ksg:cart:changed` is ours, fired
   by the add-to-cart module, which posts to cart/add.js directly and so
   raises nothing the platform would hear. It is deliberately not the
   platform event: Horizon's own components listen to that one and have no
   business re-rendering on our behalf. */
let pending;
const onChange = () => {
  /* A shopper leaning on the + button fires this several times, so the
     refreshes are coalesced into one.

     Deliberately a timer and not requestAnimationFrame. rAF is for work that
     must land before a paint, which this need not, and it is throttled to a
     stop whenever the page is not being painted - in a background tab, and
     in Shopify's own preview pane, where it never fired at all. A refresh
     that quietly stops happening is worse than the stale badge it replaced. */
  clearTimeout(pending);
  pending = setTimeout(refreshCartLink, 50);
};

document.addEventListener('shopify:cart:lines-update', onChange);
document.addEventListener('ksg:cart:changed', onChange);
